import React from 'react';
import { TrophyIcon, CalendarIcon, UserIcon, AcademicCapIcon } from '@heroicons/react/24/outline';

const CertificatePreview = ({ userData, isPreview = true }) => {
 // Sample data for preview
 const sampleData = {
 user: {
 name:"Lakshmi Prasanna Dasari",
 role:"Senior Software Engineer"
 },
 enrollment: {
 courseName:"AWS Cloud Architect",
 startDate:"15-08-2024",
 endDate:"15-11-2024"
 }
 };

 const data = userData || sampleData;

 const formatDate = (dateString) => {
 const date = new Date(dateString);
 return date.toLocaleDateString('en-GB');
 };

 return (
 <div className="min-h-screen bg-gray-100 py-8">
 <div className="max-w-4xl mx-auto">
 {/* Certificate Container */}
 <div className="bg-white shadow-2xl rounded-lg overflow-hidden">
 {/* Certificate Header */}
 <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-8 px-12 text-center">
 <div className="flex items-center justify-center mb-4">
 <div className="bg-primary rounded-full p-3 mr-4">
 <TrophyIcon className="h-12 w-12 text-blue-900" />
 </div>
 <div>
 <h1 className="text-4xl font-bold">THE SKILL GENIE</h1>
 <p className="text-primary text-lg italic">Empowering Future Minds</p>
 </div>
 </div>
 </div>

 {/* Certificate Body */}
 <div className="px-12 py-16 relative">
 {/* Decorative Border */}
 <div className="absolute inset-4 border-4 border-primary rounded-lg"></div>
 <div className="absolute inset-8 border-2 border-blue-800 rounded-lg"></div>
 
 {/* Main Content */}
 <div className="relative z-10 text-center">
 {/* Title */}
 <div className="mb-8">
 <h2 className="text-5xl font-bold text-blue-900 mb-2">CERTIFICATE</h2>
 <h3 className="text-2xl text-blue-700 font-semibold">OF ACHIEVEMENT</h3>
 </div>

 {/* Presentation Text */}
 <div className="mb-8">
 <p className="text-xl text-gray-700 mb-6">
 This certificate is proudly presented to
 </p>
 
 {/* Name */}
 <div className="mb-8">
 <h4 className="text-4xl font-bold text-blue-900 mb-2">
 {data.user.name}
 </h4>
 {data.user.role && (
 <p className="text-xl text-blue-700 font-semibold">
 As {data.user.role}
 </p>
 )}
 </div>

 {/* Achievement Text */}
 <div className="mb-8">
 <p className="text-xl text-gray-700 leading-relaxed">
 for successfully completing THE SKILL GENIE Bootcamp Certification of{' '}
 <span className="font-bold text-blue-900">{data.enrollment.courseName}</span>
 </p>
 </div>
 </div>

 {/* Dates */}
 <div className="grid grid-cols-2 gap-8 mb-12">
 <div className="text-center">
 <div className="flex items-center justify-center mb-2">
 <CalendarIcon className="h-6 w-6 text-blue-600 mr-2" />
 <span className="text-lg font-semibold text-gray-700">Starting Date</span>
 </div>
 <p className="text-xl font-bold text-blue-900">
 {formatDate(data.enrollment.startDate)}
 </p>
 </div>
 <div className="text-center">
 <div className="flex items-center justify-center mb-2">
 <CalendarIcon className="h-6 w-6 text-blue-600 mr-2" />
 <span className="text-lg font-semibold text-gray-700">Ending Date</span>
 </div>
 <p className="text-xl font-bold text-blue-900">
 {formatDate(data.enrollment.endDate)}
 </p>
 </div>
 </div>

 {/* Signature Section */}
 <div className="grid grid-cols-2 gap-8 mt-16">
 <div className="text-center">
 <div className="border-b-2 border-gray-400 mb-2 pb-1">
 <span className="text-sm text-gray-600">DATE</span>
 </div>
 <p className="text-lg font-semibold text-gray-800">
 {formatDate(data.enrollment.endDate)}
 </p>
 </div>
 <div className="text-center">
 <div className="border-b-2 border-gray-400 mb-2 pb-1">
 <span className="text-sm text-gray-600">SIGNATURE</span>
 </div>
 <p className="text-lg font-semibold text-gray-800">Balaji</p>
 </div>
 </div>
 </div>

 {/* Verification Badge */}
 <div className="absolute top-8 right-8">
 <div className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold flex items-center">
 <div className="w-3 h-3 bg-white rounded-full mr-2"></div>
 VERIFIED
 </div>
 </div>
 </div>

 {/* Certificate Footer */}
 <div className="bg-gray-50 px-12 py-6">
 <div className="flex items-center justify-between">
 <div className="flex items-center">
 <div className="bg-blue-900 rounded-full p-2 mr-3">
 <AcademicCapIcon className="h-6 w-6 text-white" />
 </div>
 <div>
 <p className="font-semibold text-gray-800">THE SKILL GENIE</p>
 <p className="text-sm text-gray-600">Certificate ID: SKG-{Date.now().toString().slice(-8)}</p>
 </div>
 </div>
 <div className="text-right">
 <p className="text-sm text-gray-600">Generated on</p>
 <p className="font-semibold text-gray-800">{new Date().toLocaleDateString()}</p>
 </div>
 </div>
 </div>
 </div>

 {/* Certificate Features */}
 {isPreview && (
 <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
 <h3 className="text-2xl font-bold text-gray-800 mb-4">Certificate Features</h3>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <div className="space-y-3">
 <h4 className="text-lg font-semibold text-blue-900">Design Elements</h4>
 <ul className="space-y-2 text-gray-700">
 <li className="flex items-center">
 <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
 Professional blue and gold color scheme
 </li>
 <li className="flex items-center">
 <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
 Decorative borders and gradients
 </li>
 <li className="flex items-center">
 <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
 Trophy icon and branding
 </li>
 <li className="flex items-center">
 <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
 Verification badge
 </li>
 </ul>
 </div>
 <div className="space-y-3">
 <h4 className="text-lg font-semibold text-blue-900">Content</h4>
 <ul className="space-y-2 text-gray-700">
 <li className="flex items-center">
 <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
 User's full name prominently displayed
 </li>
 <li className="flex items-center">
 <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
 Course name and role information
 </li>
 <li className="flex items-center">
 <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
 Start and end dates
 </li>
 <li className="flex items-center">
 <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
 Unique certificate ID
 </li>
 </ul>
 </div>
 </div>
 </div>
 )}
 </div>
 </div>
 );
};

export default CertificatePreview;









